import React, { type JSX } from 'react';
import ArchonLayout, {
  Background,
  Container,
} from '../../layouts/ArchonLayout/ArchonLayout';
import Seo from '../Seo/Seo';
import { type ArchonHeaderViewModel } from '../ArchonHeader/ArchonHeader';
import { type ArchonFooterViewModel } from '../ArchonFooter/ArchonFooter';
import {
  type DownloadPageGame,
  type DownloadPageViewModel,
} from '../../helpers/getDownloadPageViewModels';
import DownloadButton, {
  OsIcon,
} from '@resources/js/components/DownloadButton/DownloadButton';
import Image from 'next/image';
import OptimizedImage from '../OptimizedImage/OptimizedImage';
import { assetServerBaseUrl } from '@resources/js/constants/assetServer';
import styles from './DownloadPage.module.scss';
import { type UserAgentOS, useOs } from '@resources/js/hooks/useOs';
import { V3Theme } from '@resources/js/contexts/useTheme';
import Body from '@resources/js/components/Body/Body';
import Heading from '@resources/js/components/Heading/Heading';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import BadgeLabel from '@resources/js/components/BadgeLabel/BadgeLabel';

type DownloadPageProps = {
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  page: DownloadPageViewModel;
};

export default function DownloadPage({
  header,
  footer,
  page,
}: DownloadPageProps): JSX.Element {
  const { seo, games } = page;
  const os = useOs();

  return (
    <ArchonLayout header={header} footer={footer} theme={V3Theme}>
      <Seo {...seo} />
      <Background />
      <Container>
        <VerticalContent gap={'medium'} className={styles.hero}>
          <Heading level={1} as={'span'}>
            {trans('archon.download_page_hero_heading')}
          </Heading>
          <h1 className={styles.appName}>Archon App</h1>
          <p className={styles.subheading}>
            {trans('archon.download_page_subheading')}
          </p>
          <DownloadOsLinks os={os} page={page} />
        </VerticalContent>

        <Body>
          <VerticalContent gap={'xlarge'}>
            <FeatureSection
              heading={trans('archon.download_feature_logging_heading')}
              body={trans('archon.download_feature_logging_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-app-logging.jpg`}
              comingSoonText={trans(
                'archon.download_feature_logging_coming_soon_text',
              )}
              games={games}
            />
            <FeatureSection
              heading={trans('archon.download_feature_vod_heading')}
              body={trans('archon.download_feature_vod_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-video.jpg?v=2`}
              isReverse
              supportedPlatforms={os !== 'win' ? ['win'] : undefined}
              supportedGames={['wow', 'ffxiv', 'fellowship']}
              comingSoonGames={['eso', 'swtor']}
              games={games}
            />
            <FeatureSection
              heading={trans('archon.download_feature_analysis_heading')}
              body={trans('archon.download_feature_analysis_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-app-postpull.jpg`}
              games={games}
            />
            <FeatureSection
              heading={trans('archon.download_feature_group_finder_heading')}
              body={trans('archon.download_feature_group_finder_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-app-groupfinder.jpg`}
              isReverse
              supportedGames={['wow', 'ffxiv']}
              supportedPlatforms={os !== 'win' ? ['win'] : undefined}
              games={games}
            />
            <FeatureSection
              heading={trans('archon.download_feature_meters_heading')}
              body={trans('archon.download_feature_meters_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-app-meters.jpg?v=2`}
              supportedGames={['ffxiv', 'swtor']}
              games={games}
            />
            <FeatureSection
              heading={trans('archon.download_feature_meta_heading')}
              body={trans('archon.download_feature_meta_body')}
              imageSrc={`${assetServerBaseUrl}/img/archon/resource-download-app-meta.jpg`}
              isReverse
              isComingSoon
              supportedGames={['wow', 'ffxiv', 'fellowship']}
              games={games}
            />
            <DownloadBottomBlock os={os} page={page} />
          </VerticalContent>
        </Body>
      </Container>
    </ArchonLayout>
  );
}

type FeatureSectionProps = {
  heading: string;
  body: string;
  imageSrc?: string;
  isReverse?: boolean;
  isComingSoon?: boolean;
  comingSoonText?: string;
  supportedGames?: string[];
  supportedPlatforms?: UserAgentOS[];
  comingSoonGames?: string[];
  games: Record<string, DownloadPageGame>;
  children?: React.ReactNode;
};

function FeatureSection({
  heading,
  body,
  imageSrc,
  isReverse = false,
  isComingSoon = false,
  comingSoonText,
  comingSoonGames = [],
  supportedGames = [],
  supportedPlatforms = [],
  games,
  children,
}: FeatureSectionProps): JSX.Element {
  return (
    <div
      className={`${styles.feature} ${isReverse ? styles.featureReverse : ''}`}
    >
      <VerticalContent gap={'smaller'} className={styles.featureText}>
        <HorizontalContent gap={'smaller'} className={styles.featureHeading}>
          <Heading level={2} styleLevel={1}>
            {heading}
          </Heading>
          {isComingSoon && (
            <BadgeLabel style={'level-2'}>
              {trans('global.coming_soon_titlecase')}
            </BadgeLabel>
          )}
        </HorizontalContent>
        <p>{body}</p>
        {comingSoonText && (
          <p className={styles.comingSoonText}>
            <BadgeLabel
              style={'orange'}
            >{`${trans('global.new')}!`}</BadgeLabel>{' '}
            {comingSoonText}
          </p>
        )}
        <GameList
          label={trans('archon.supported_games')}
          slugs={supportedGames}
          games={games}
        />
        <GameList
          label={trans('global.coming_soon_titlecase')}
          slugs={comingSoonGames}
          games={games}
        />
        {supportedPlatforms.length > 0 ? (
          <HorizontalContent className={styles.onlyAvailableOnText}>
            <span>{trans('archon.download_only_available_on_platform')}</span>
            {supportedPlatforms.map((os) => (
              <HorizontalContent
                key={os}
                className={styles.onlyAvailableOsLabel}
                gap={'tiny'}
              >
                <OsIcon os={os} /> {osName(os)}
              </HorizontalContent>
            ))}
          </HorizontalContent>
        ) : null}
        {children}
      </VerticalContent>
      {imageSrc && (
        <div className={styles.featureMedia}>
          <Image
            src={imageSrc}
            alt={heading}
            fill
            style={{ objectFit: 'cover' }}
          />
        </div>
      )}
    </div>
  );
}

function GameList({
  label,
  slugs,
  games,
}: {
  label: string;
  slugs: string[];
  games: Record<string, DownloadPageGame>;
}): JSX.Element | null {
  if (slugs.length === 0) {
    return null;
  }

  return (
    <HorizontalContent>
      <span>{label}:</span>
      <HorizontalContent gap={'small'}>
        {slugs.map((slug) => {
          const game = games[slug];
          return game ? (
            <OptimizedImage
              key={slug}
              src={game.imageUrl}
              alt={slug}
              width={28}
              height={28}
              isZoomable={false}
              includeMargin={false}
            />
          ) : null;
        })}
      </HorizontalContent>
    </HorizontalContent>
  );
}

function osName(os: UserAgentOS): string {
  return os === 'linux'
    ? trans('global.os_linux')
    : os === 'mac'
      ? trans('global.os_macos')
      : trans('global.os_windows');
}

function DownloadBottomBlock({
  page,
  os,
}: {
  os: UserAgentOS;
  page: DownloadPageViewModel;
}): JSX.Element | null {
  const liteDownloadUrl =
    os === 'linux'
      ? page.liteApp?.linuxDownloadUrl
      : os === 'mac'
        ? page.liteApp?.macDownloadUrl
        : page.liteApp?.windowsDownloadUrl;

  return (
    <VerticalContent>
      <div className={styles.downloadBlock}>
        <Heading level={2} styleLevel={1}>
          {trans('archon.download_the_archon_app')}
        </Heading>
        <p style={{ textAlign: 'center' }}>
          {trans('archon.download_page_subheading')}
        </p>
        <DownloadOsLinks os={os} page={page} />
      </div>
      {liteDownloadUrl && (
        <span className={styles.archonLiteDownloadText}>
          {trans('archon.archon_app_lite_desc')}{' '}
          <a href={liteDownloadUrl}>
            {trans('archon.archon_app_lite_download_text')}
          </a>
        </span>
      )}
    </VerticalContent>
  );
}

function DownloadOsLinks({
  page,
  os,
}: {
  os: UserAgentOS;
  page: DownloadPageViewModel;
}): JSX.Element {
  const primaryUrl =
    os === 'mac'
      ? page.macDownloadUrl
      : os === 'linux'
        ? page.linuxDownloadUrl
        : page.windowsDownloadUrl;
  return (
    <>
      <div>
        <DownloadButton href={primaryUrl} forceOs={os}>
          {trans('archon.download_for_os', { os: osName(os) })}
        </DownloadButton>
      </div>
      <HorizontalContent gap={'large'}>
        {os !== 'win' && (
          <a href={page.windowsDownloadUrl} className={styles.secondaryLink}>
            {trans('global.os_windows')}
          </a>
        )}
        {os !== 'mac' && (
          <a href={page.macDownloadUrl} className={styles.secondaryLink}>
            {trans('global.os_macos')}
          </a>
        )}
        {os !== 'linux' && (
          <a href={page.linuxDownloadUrl} className={styles.secondaryLink}>
            {trans('global.os_linux')}
          </a>
        )}
      </HorizontalContent>
    </>
  );
}
